@use 'sass:math';
@use 'pkg:@ongov/ontario-design-system-global-styles/styles/scss/1-variables/global.variables' as globalVariables;
@use 'pkg:@ongov/ontario-design-system-global-styles/styles/scss/1-variables/spacing.variables' as spacing;
@use 'pkg:@ongov/ontario-design-system-global-styles/styles/scss/1-variables/colours.variables' as colours;
@use 'pkg:@ongov/ontario-design-system-global-styles/styles/scss/1-variables/breakpoints.variables' as breakpoints;
@use 'pkg:@ongov/ontario-design-system-global-styles/styles/scss/2-tools/placeholder/focus.placeholders' as placeholders;
@use 'pkg:@ongov/ontario-design-system-global-styles/styles/scss/1-variables/font-weights.variables' as fontWeights;
@use 'pkg:@ongov/ontario-design-system-global-styles/styles/scss/1-variables/font-sizes.variables' as fontSizes;
@use 'pkg:@ongov/ontario-design-system-global-styles/styles/scss/2-tools/functions/global.functions' as globalFunctions;
@use 'pkg:@ongov/ontario-design-system-global-styles/styles/scss/1-variables/typography.variables' as typography;

.ontario-card {
	box-shadow: 0rem 0.1875rem 0.5rem 0.0625rem rgba(0, 0, 0, 0.4);
	border-radius: globalVariables.$global-radius;
	margin-bottom: spacing.$spacing-7;
	padding: spacing.$spacing-0;
	overflow: hidden;
	/*
	 * Required for cards to stay the same height regardless of content size.
	 * Without subtracting the margin-bottom amount the cards will be too large.
	 */
	height: calc(100% - spacing.$spacing-7);
	list-style-type: none;
	transition: all 0.3s ease-in-out;
	position: relative;
	cursor: pointer;
	background: colours.$ontario-colour-white;

	&:hover {
		box-shadow: 0rem 0.375rem 0.75rem 0.125rem rgba(0, 0, 0, 0.35);
	}

	&:focus-within {
		box-shadow: placeholders.$ontario-focus-box-shadow;
		outline: 4px solid transparent;
		transition: box-shadow 0.1s ease-in-out;
	}

	&:active {
		box-shadow: placeholders.$ontario-focus-box-shadow;
		background-color: colours.$ontario-greyscale-5;
	}

	@media screen and (max-width: breakpoints.$small-breakpoint) {
		margin-bottom: spacing.$spacing-8;
		width: 100% !important; // to override the specificity of the widths listed for the card-rows
		max-width: 100%;
	}
}

.ontario-card--image-true {
	margin-top: spacing.$spacing-2 * -1;
	@at-root .ontario-card__card-type--horizontal & {
		margin-top: spacing.$spacing-0;
	}
}

.ontario-card__card-type--horizontal {
	display: flex;

	@media screen and (max-width: breakpoints.$small-breakpoint) {
		width: 100%;
		max-width: 100%;
	}

	.ontario-card__image-container,
	.ontario-card__text-container {
		position: relative;
	}

	.ontario-card__heading {
		border-radius: 0;
	}

	a {
		&:focus-within {
			box-shadow: none;
			outline: none;
		}

		&:active {
			box-shadow: none;
		}
	}

	img {
		height: 100%;
		position: absolute;
	}
}

.ontario-card__card-type--basic {
	display: flex;
	flex-direction: column;
}

.ontario-card__image-right {
	flex-direction: row-reverse;
}

.ontario-card__heading {
	@extend %h4-styles;
	margin: spacing.$spacing-0;
	border-radius: globalVariables.$global-radius globalVariables.$global-radius 0 0;
	padding: (spacing.$spacing-5 - spacing.$spacing-1) spacing.$spacing-5 spacing.$spacing-2 spacing.$spacing-5;
	transition: text-decoration 0.3s ease-in-out;
	background-color: colours.$ontario-colour-white;
	max-width: none;

	.ontario-card:hover & {
		text-decoration-line: underline;
		text-decoration-color: colours.$ontario-colour-black;
	}

	.ontario-card--image-true & {
		border-radius: 0;
		@at-root .ontario-card__image-right & {
			border-radius: globalVariables.$global-radius 0 0 0;
		}

		@at-root .ontario-card__image-left & {
			border-radius: 0 globalVariables.$global-radius 0 0;
		}
	}

	a {
		color: colours.$ontario-colour-black;
		text-decoration: none;
		outline: none;

		&::after {
			content: '';
			position: absolute;
			left: 0;
			top: 0;
			right: 0;
			bottom: 0;
		}

		&:focus {
			box-shadow: none;
		}

		&:active {
			outline: none;
		}
	}

	@media screen and (max-width: breakpoints.$medium-breakpoint) {
		font-size: fontSizes.$ontario-font-size-large;
		padding-left: spacing.$spacing-4;
		padding-right: spacing.$spacing-4;
	}
}

// ontario-card's header colour options

$ontario-card-heading-colours: (
	'light-accent': colours.$ontario-greyscale-5,
	'dark-accent': colours.$ontario-colour-black,
	'light-gold': colours.$ontario-colour-light-gold,
	'light-yellow': colours.$ontario-colour-light-yellow,
	'light-taupe': colours.$ontario-colour-light-taupe,
	'light-green': colours.$ontario-colour-light-green,
	'light-lime': colours.$ontario-colour-light-lime,
	'light-teal': colours.$ontario-colour-light-teal,
	'light-sky': colours.$ontario-colour-light-sky,
	'light-purple': colours.$ontario-colour-light-purple,
	'light-orange': colours.$ontario-colour-light-orange,
	'light-red': colours.$ontario-colour-light-red,
	'light-magenta': colours.$ontario-colour-light-magenta,
	'gold': colours.$ontario-colour-gold,
	'yellow': colours.$ontario-colour-yellow,
	'taupe': colours.$ontario-colour-taupe,
	'green': colours.$ontario-colour-green,
	'lime': colours.$ontario-colour-lime,
	'teal': colours.$ontario-colour-teal,
	'sky': colours.$ontario-colour-sky,
	'purple': colours.$ontario-colour-purple,
	'orange': colours.$ontario-colour-orange,
	'red': colours.$ontario-colour-red,
	'magenta': colours.$ontario-colour-magenta,
);

@each $colour-name, $colour-value in $ontario-card-heading-colours {
	.ontario-card__background--#{$colour-name} {
		background-color: $colour-value;
	}

	.ontario-card__heading--#{$colour-name} {
		background-color: $colour-value;
		padding-top: spacing.$spacing-4;
		padding-bottom: spacing.$spacing-4;
	}
}

.ontario-card__text-container {
	background-color: colours.$ontario-colour-white;

	.ontario-card__card-type--basic & {
		display: flex;
		flex: 1 1 auto;
		flex-direction: column;
	}

	.ontario-card__card-type--horizontal & {
		width: math.percentage(math.div(2, 3));
	}

	.ontario-card__image-size-one-fourth & {
		width: 75%;
	}

	.ontario-card--no-image & {
		width: 100%;
	}
}

.ontario-card__description {
	margin: spacing.$spacing-0;
	padding: spacing.$spacing-2 spacing.$spacing-5 spacing.$spacing-5 spacing.$spacing-5;
	background-color: colours.$ontario-colour-white;
	border-bottom-left-radius: globalVariables.$global-radius;
	border-bottom-right-radius: globalVariables.$global-radius;
	.ontario-card__card-type--basic & {
		flex: 1 1 auto;
	}

	p {
		margin-top: spacing.$spacing-0;
	}

	p:last-of-type {
		margin-bottom: spacing.$spacing-0;
	}

	@media screen and (max-width: breakpoints.$medium-breakpoint) {
		padding: spacing.$spacing-2 spacing.$spacing-4 spacing.$spacing-5 spacing.$spacing-4;
	}
}

.ontario-card__image-container {
	a {
		&:focus {
			outline: none;
			box-shadow: none;
		}
	}
	.ontario-card--position-vertical & {
		margin-bottom: spacing.$spacing-2 * -1;
	}

	.ontario-card__card-type--horizontal & {
		width: 33.3%;
	}

	.ontario-card__image-size-one-fourth & {
		width: 25%;
	}
}

.ontario-card__image {
	width: 100%;
	height: 100%;
	object-fit: cover;
	background-size: 100% 100%;

	.ontario-card--position-vertical & {
		border-top-left-radius: globalVariables.$global-radius;
		border-top-right-radius: globalVariables.$global-radius;
	}

	.ontario-card__image-right & {
		border-top-right-radius: globalVariables.$global-radius;
		border-bottom-right-radius: globalVariables.$global-radius;
	}

	.ontario-card__image-left & {
		border-top-left-radius: globalVariables.$global-radius;
		border-bottom-left-radius: globalVariables.$global-radius;
	}
}
